import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { selectByCategory } from '@/lib/placeFilter';
import { parseOutsideSort, sortOutsidePlaces, type OutsideSort } from '@/lib/outsidePlaceSort';
import { usePlaces } from '@/hooks/usePlaces';
import { useFavorites } from '@/hooks/useFavorites';
import { PlaceCard } from '@/components/PlaceCard';
import { CheckIcon, SearchIcon } from '@/components/Icons';
import { LocalityFilterRow } from '@/components/LocalityFilterRow';
import {
  groupByLocality,
  LOCALITY_LABELS,
  LOCALITY_BLURBS,
  parseLocalityFilter,
  type LocalityFilter,
  type LocalityGroup,
} from '@shared/locality';

const CATEGORIES: { key: string; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'food', label: 'Food' },
  { key: 'grocery', label: 'Grocery' },
  { key: 'medical', label: 'Medical' },
  { key: 'shopping', label: 'Shopping' },
  { key: 'home_improvement', label: 'Home & Hardware' },
  { key: 'services', label: 'Services' },
  { key: 'fitness', label: 'Fitness' },
  { key: 'recreation', label: 'Recreation' },
  { key: 'attraction', label: 'Attraction' },
  { key: 'gas', label: 'Gas' },
  { key: 'travel', label: 'Travel' },
  { key: 'explore', label: 'Explore' },
];

/** Reads ?locality= / ?category= / ?open= / ?sort= so other tabs can deep-link here.
    Open Now used to be a Home feed card; Places owns that list now.
    `sort` applies only to the Outside the Ranch list. */
function initialFilters(search: string): {
  locality: LocalityFilter;
  category: string;
  openNow: boolean;
  outsideSort: OutsideSort;
} {
  const q = new URLSearchParams(search);
  const cat = q.get('category');
  return {
    locality: parseLocalityFilter(q.get('locality')),
    category: cat && cat.trim() ? cat : 'all',
    openNow: q.get('open') === 'now',
    outsideSort: parseOutsideSort(q.get('sort')),
  };
}

export function PlacesPage() {
  const { places, loading } = usePlaces();
  const { isFavorite, toggle } = useFavorites();
  const { search } = useLocation();
  const navigate = useNavigate();
  const initial = useMemo(() => initialFilters(search), [search]);
  const [category, setCategory] = useState(initial.category);
  const [locality, setLocality] = useState<LocalityFilter>(initial.locality);
  const [openNow, setOpenNow] = useState(initial.openNow);
  const [outsideSort, setOutsideSort] = useState<OutsideSort>(initial.outsideSort);

  // Follow the URL when it changes (e.g. the user taps a different deep
  // link while already on this page, which does not remount the component).
  useEffect(() => {
    setLocality(initial.locality);
    setCategory(initial.category);
    setOpenNow(initial.openNow);
    setOutsideSort(initial.outsideSort);
  }, [initial]);

  // Mirror the current filters into the URL (replacing, not pushing, so
  // every chip tap doesn't add a back-stack entry). Without this, opening a
  // place's detail page and coming back landed on the bare "/places" this
  // page started from — before any filter was chosen — dropping "In Ranch"
  // + "Food" back to "Everywhere" + "All" every time.
  useEffect(() => {
    const q = new URLSearchParams();
    if (locality !== 'both') q.set('locality', locality);
    if (category !== 'all') q.set('category', category);
    if (openNow) q.set('open', 'now');
    // Distance is the list's existing order, so it stays out of the URL
    // until Alphabetical is chosen.
    if (outsideSort === 'alphabetical') q.set('sort', 'alphabetical');
    const nextSearch = q.toString();
    if (nextSearch !== search.replace(/^\?/, '')) {
      navigate({ pathname: '/places', search: nextSearch }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locality, category, openNow, outsideSort]);

  const [filterText, setFilterText] = useState('');

  const filtered = useMemo(() => {
    if (!places) return [];
    // Babcock National / Babcock Ranch places already have a dedicated home
    // on the Babcock tab; keep this general browse list focused on the
    // categories its own filter chips cover, so nothing shows up twice
    // with no way to tell why it's here.
    let list = selectByCategory(places, category);
    if (openNow) {
      list = list.filter((p) => p.open_state.isOpen);
    }
    if (filterText.trim()) {
      const q = filterText.toLowerCase();
      list = list.filter(
        (p) => p.name.toLowerCase().includes(q) || p.tags.some((t) => t.toLowerCase().includes(q)),
      );
    }
    return [...list].sort((a, b) => (a.distance_from_home_miles ?? 0) - (b.distance_from_home_miles ?? 0));
  }, [places, category, filterText, openNow]);

  const grouped = useMemo(() => groupByLocality(filtered), [filtered]);
  // Inside the Ranch keeps the distance order from `filtered`. Only the
  // outside list follows the sort buttons.
  const outsideSorted = useMemo(
    () => sortOutsidePlaces(grouped.outside, outsideSort),
    [grouped.outside, outsideSort],
  );

  // Which groups to actually render, in order. Inside always comes first —
  // it is the answer to the most common question.
  const visibleGroups: LocalityGroup[] =
    locality === 'both' ? ['inside', 'outside'] : [locality];

  const totalShown = visibleGroups.reduce((n, g) => n + grouped[g].length, 0);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Places</h1>
        <p className="page-subtitle">Curated, verified, and close to home</p>
      </div>

      {/* Sticky bar: search input + filter chips remain reachable as the
          list scrolls. The `.sticky-bar` background matches the page so
          scrolled content doesn't bleed through. */}
      <div className="places-sticky">
        <div className="search-box" style={{ marginBottom: 8 }}>
          <SearchIcon />
          <label htmlFor="places-filter" className="visually-hidden">
            Filter places
          </label>
          <input
            id="places-filter"
            type="search"
            placeholder="Filter places"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        {/* Locality segment sits above category, because inside-vs-outside
            is the coarser and more consequential cut. */}
        <LocalityFilterRow
          value={locality}
          onChange={setLocality}
          loading={loading}
          counts={{ inside: grouped.inside.length, outside: grouped.outside.length }}
        />

        <div className="chip-row" role="group" aria-label="Open now">
          <button
            type="button"
            className={`chip ${openNow ? 'chip--active' : ''}`}
            aria-pressed={openNow}
            onClick={() => setOpenNow((v) => !v)}
          >
            Open now
          </button>
        </div>

        {/* Same outlined checkbox pills as Favorites “What I care about”.
            Single-select (including All) — not the place-row InterestCheck. */}
        <div className="chip-row" role="tablist" aria-label="Category">
          {CATEGORIES.map((c) => {
            const selected = category === c.key;
            return (
              <button
                key={c.key}
                type="button"
                role="tab"
                aria-selected={selected}
                className={`interest-chip ${selected ? 'interest-chip--on' : ''}`}
                onClick={() => setCategory(c.key)}
              >
                <CheckIcon checked={selected} />
                <span>{c.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {loading && (
        <div className="stack-gap">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton" style={{ height: 140 }} />
          ))}
        </div>
      )}

      {!loading && totalShown === 0 && <p className="empty-state">No places match yet.</p>}

      {!loading &&
        visibleGroups.map((group) => {
          const items = group === 'outside' ? outsideSorted : grouped[group];
          if (items.length === 0) return null;
          return (
            <section key={group} className="locality-section" aria-labelledby={`locality-${group}`}>
              <div className="locality-header">
                <h2 id={`locality-${group}`} className="locality-header__title">
                  {LOCALITY_LABELS[group]}
                  <span className="locality-header__count">{items.length}</span>
                </h2>
                <p className="locality-header__blurb">{LOCALITY_BLURBS[group]}</p>
              </div>
              {group === 'outside' && (
                <div className="outside-sort" role="group" aria-label="Sort">
                  <button
                    type="button"
                    className={`chip ${outsideSort === 'distance' ? 'chip--active' : ''}`}
                    aria-pressed={outsideSort === 'distance'}
                    onClick={() => setOutsideSort('distance')}
                  >
                    Distance
                  </button>
                  <button
                    type="button"
                    className={`chip ${outsideSort === 'alphabetical' ? 'chip--active' : ''}`}
                    aria-pressed={outsideSort === 'alphabetical'}
                    onClick={() => setOutsideSort('alphabetical')}
                  >
                    Alphabetical
                  </button>
                </div>
              )}
              {items.map((p) => (
                <PlaceCard key={p.id} place={p} isFavorite={isFavorite(p.id)} onToggleFavorite={toggle} />
              ))}
            </section>
          );
        })}
    </div>
  );
}
